import { useEffect, useState } from 'react';
import { WifiOff, RotateCcw, Phone, Mail, LifeBuoy } from 'lucide-react';
import { PORTAL_BRAND } from '../../data/brand';
import { formatSilence } from '../../lib/banepadHeartbeat';
import type { PadLastKnown } from '../../lib/banepadLastKnown';

export interface BanepadDeviceOfflineProps {
  courtName: string;
  /** Epoch ms when the outage started. */
  since: number;
  onRetry: () => void;
  /**
   * Support contact shown on the screen. Defaults to the brand's
   * (PORTAL_BRAND.support). Phone/e-mail are optional: until they are decided
   * the screen still says WHO to contact, just without a number.
   */
  support?: { phone?: string; email?: string };
  /**
   * What this court was last seen doing, when the pad has a note of it (#479).
   *
   * A REPORT, never a court view. It renders as text inside this screen and
   * nothing in it is pressable, so nothing can be recorded against a match the
   * server may since have finalised — see lib/banepadLastKnown.ts for why that
   * boundary is where it is.
   */
  lastKnown?: PadLastKnown | null;
}

/**
 * Full-screen overlay the pad shows when it has lost contact with Starpoint
 * for a while: what is wrong, that it will recover by itself, how long it has
 * been out, and who to call — so players are not the ones who have to report
 * it. Rendered OVER the pad (which keeps running underneath) and disappears
 * on its own once heartbeats succeed again.
 */
export function BanepadDeviceOffline({
  courtName,
  since,
  onRetry,
  support = PORTAL_BRAND.support,
  lastKnown = null,
}: BanepadDeviceOfflineProps) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);
  const phone = support?.phone || '';
  const email = support?.email || '';
  return (
    <div
      role="alert"
      // FITS A LANDSCAPE TABLET (#1102). With the last-known match and both
      // support contacts this adds up to roughly 860px, more than a 768px pad
      // in landscape, and `justify-center`
      // cut it off at BOTH ends with neither reachable — the «Prøv igjen» button
      // below the bottom. It now scrolls, and is centred by auto margins on the
      // first and last child instead: they centre it exactly as before while it
      // fits, and collapse to zero when it does not, so the overflow goes to the
      // bottom where it can be scrolled to. `py-8` is the gap once they do.
      className="fixed inset-0 flex flex-col items-center overflow-y-auto text-center px-8 py-8"
      style={{ background: 'rgba(10, 11, 16, 0.96)', color: '#e6fbff', zIndex: 50 }}
    >
      <div className="mt-auto" style={{ color: '#f59e0b' }}>
        <WifiOff size={72} />
      </div>
      <h1 className="font-display font-bold mt-5" style={{ fontSize: 44, lineHeight: 1.1 }}>
        Ingen forbindelse
      </h1>
      <p className="mt-3" style={{ fontSize: 22, color: '#b8c4c0', maxWidth: 720 }}>
        {courtName} har mistet kontakten med Starpoint og prøver å koble til igjen automatisk. Sjekk
        at nettbrettet har nett.
      </p>
      <p
        className="font-mono uppercase tracking-wider mt-4"
        style={{ fontSize: 16, color: '#94a098' }}
      >
        Uten kontakt i {formatSilence(now - since)}
      </p>

      {/* ── WHAT THE COURT WAS DOING (#479) ──────────────────────────
          Only when the pad has a note. Players standing at the court can at
          least see the match they are in; without this the screen says the
          tablet is broken and nothing about the padel.

          Everything here is LABELLED AS OLD — the heading, the timestamp and
          «ikke oppdatert» — because the one thing worse than no score is a
          stale one a reader takes for live. */}
      {lastKnown && (
        <div
          className="mt-8 rounded-2xl px-8 py-5"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px dashed rgba(255,255,255,0.18)',
            maxWidth: 720,
          }}
        >
          <div
            className="font-mono uppercase tracking-wider"
            style={{ fontSize: 13, color: '#94a098' }}
          >
            Sist kjent · {formatClock(lastKnown.savedAt)}
          </div>
          {lastKnown.title && (
            <div className="mt-2" style={{ fontSize: 18, color: '#b8c4c0' }}>
              {lastKnown.title}
            </div>
          )}
          {lastKnown.players.length > 0 && (
            <div className="mt-2 font-semibold" style={{ fontSize: 26 }}>
              {lastKnown.players.join(' · ')}
            </div>
          )}
          {lastKnown.score && (
            <div className="mt-1 font-display font-bold" style={{ fontSize: 34 }}>
              {lastKnown.score}
            </div>
          )}
          <div className="mt-3" style={{ fontSize: 16, color: '#94a098' }}>
            {lastKnown.live ? 'Kampen pågikk' : 'Kampen var neste'} — ikke oppdatert etter dette
          </div>
        </div>
      )}

      {/* Always present: the point of the screen is that nobody has to work out
          who to call. Phone/e-mail appear here once they are decided. */}
      <div
        className="mt-8 rounded-2xl px-8 py-5"
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        <div
          className="font-mono uppercase tracking-wider"
          style={{ fontSize: 13, color: '#94a098' }}
        >
          Hjelp
        </div>
        <div className="mt-2 flex flex-col gap-2 items-start" style={{ fontSize: 22 }}>
          <span className="inline-flex items-center gap-3">
            <LifeBuoy size={20} /> Kontakt {PORTAL_BRAND.name} support
          </span>
          {phone && (
            <span className="inline-flex items-center gap-3">
              <Phone size={20} /> {phone}
            </span>
          )}
          {email && (
            <span className="inline-flex items-center gap-3">
              <Mail size={20} /> {email}
            </span>
          )}
        </div>
      </div>

      <button
        onClick={onRetry}
        className="btn-lime mt-8 mb-auto justify-center"
        style={{ padding: '0.9rem 1.6rem', fontSize: 18 }}
      >
        <RotateCcw size={18} /> Prøv igjen nå
      </button>
    </div>
  );
}

/**
 * `HH:MM` for the note's timestamp, in the tablet's own locale.
 *
 * DISPLAY ONLY, and nothing derived from it is stored or compared — the note
 * itself carries epoch ms, which is what `readPadLastKnown` judges age by
 * (DOMAIN_RULES §4).
 */
function formatClock(epochMs: number): string {
  try {
    return new Date(epochMs).toLocaleTimeString('nb-NO', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}
